<!-- @format -->
<script lang="ts">
  import { getStoresContext } from "~/stores";
  import type { StoresKey } from "~/stores";
  import type { ManageAPI } from "~/manage-api/types";
  import { groupByInitial, belongsToFob } from "~/util/store";
  import { getFragmentConfigOr } from "~/components/field-utils";
  import { CHILDREN_FOB_ID, getFobs } from "../../configuration";
  import { onMount } from "svelte";
  import ToggleButtonGroup from "./ToggleButtonGroup.svelte";
  import StoreCard from "./StoreCard.svelte";
  import StoreCategory from './StoreCategory.svelte';
  import StoreOpeningHours from './StoreOpeningHours.svelte';
  
  export let request_store_key: StoresKey;
  export let fragment: Fragment;

  const stores_context = getStoresContext(request_store_key);
  const { stores, locale, T, scdata } = stores_context;
  const { getConfigWebContent } = stores_context.fieldUtils();
  const { childrenStores } = getConfigWebContent("childrenStores", fragment);
  const lineOfBusinessID: number = getFragmentConfigOr("lineOfBusinessID", fragment, 0);
  $: t = $T;
  $: fob_selected = lineOfBusinessID ? 
    [{ id: 'all_floor', type: 'floor' }] : 
    [{ id: 'toggle_all', type: 'fob' }, { id: 'all_floor', type: 'floor' }];

  let display = '';
  let stores_filtered:ManageAPI.Store[] = [];
  let stores_by_initial :any
  let search_term = '';

  let page = 1;
  let size = 45;
  let stores_shown: ManageAPI.Store[] = [];
  let infinite_id = +new Date();

  const childrenStoresIDs = typeof childrenStores === 'string' 
    ? childrenStores
        .replace(/\s+/g, "")
        .split(",")
        .map((i: string) => Number(i))
    : [];

  onMount(()=> {
    //Check site url for which view to display (normal/alphabetical-order/opening-hours)
    display = new URLSearchParams(location.search).get('display') || 'normal';
  })

  function changeFilter(event: any) {
    search_term = '';
    fob_selected = event.detail;
  }
  function changeSearch(e: any) {
    search_term = e.detail;
  }

  function filterByFloor(fob_selected: any[]) {
    const floors = fob_selected
      .filter((i: any) => i.type === 'floor' && i.id !== 'all_floor')
      .map((i: any) => i.id);
    
    if (floors.length) {
      stores_filtered = stores_filtered.filter((store: ManageAPI.Store) => {
        if (!store.floor) return false;
        
        // Handle type mismatches by converting both to string for comparison
        const storeFloorId = String(store.floor.id);
        return floors.some(floorId => String(floorId) === storeFloorId);
      });
    }
  }
$: {
  const fobs = $scdata && getFobs($scdata);
  
  $stores &&
    $stores.forEach((store: ManageAPI.Store) => {
      const matched = childrenStoresIDs.filter((id: number) => id === store.id).length;
      if (matched) {
        const { additionalFieldOfBusinessIds = [] } = store;
        additionalFieldOfBusinessIds.push(CHILDREN_FOB_ID);
        store.additionalFieldOfBusinessIds = additionalFieldOfBusinessIds;
      }
    });
    
  if($locale && $stores.length){  
    if (lineOfBusinessID && fobs && fobs.length > 0) {
      // Find the corresponding FOB with its internal fob_ids
      const matchingFob = fobs.find(fob => fob.id === lineOfBusinessID);
      
      if (matchingFob) {
        // If it's a subpage of services page with a matched FOB:
        // Direct filter with the internal fob_ids
        stores_filtered = [...$stores].filter(store => {
          const storeFobs = store.additionalFieldOfBusinessIds ? 
            [...store.additionalFieldOfBusinessIds, store.fieldOfBusinessId] : 
            [store.fieldOfBusinessId];
          
          return matchingFob.fob_ids.some(fobId => storeFobs.includes(fobId));
        });
        
        if (stores_filtered.length === 0) {
          console.error(`No stores found for FOB ID ${lineOfBusinessID} (internal IDs: ${matchingFob.fob_ids.join(', ')})`);
        }
      } else {
        console.error(`No FOB configuration found for lineOfBusinessID: ${lineOfBusinessID}. Check that this ID exists in b2c_fobs.`);
        stores_filtered = [];
      }
      
      //filter by FLOOR
      filterByFloor(fob_selected);
    } else {
      if (fob_selected.length) {    
        if (fob_selected[0].id === 'toggle_all') {
          stores_filtered = [...$stores];
        } else {
          const fobsFilter = fob_selected
            .filter((i: any) => i.type === 'fob' && i.id !== 'toggle_all')
            .map((i: any) => i.id);
            
            if (fobsFilter.length && fobs && fobs.length > 0) {
              // Find the selected FOB configs
              const selectedFobs = fobs.filter(fob => fobsFilter.includes(fob.id));
              
              if (selectedFobs.length > 0) {
                // Directly filter using the FOB internal IDs
                const allInternalFobIds = selectedFobs.flatMap(fob => fob.fob_ids);
                
                stores_filtered = [...$stores].filter(store => {
                  const storeFobs = store.additionalFieldOfBusinessIds ? 
                    [...store.additionalFieldOfBusinessIds, store.fieldOfBusinessId] : 
                    [store.fieldOfBusinessId];
                  
                  return allInternalFobIds.some(fobId => storeFobs.includes(fobId));
                });
              } else {
                // Log error if no matching FOB configuration is found
                console.error(`No FOB configuration found for IDs: ${fobsFilter.join(', ')}. Check configuration.ts and b2c_fobs data.`);
                stores_filtered = []; // Empty results to show something is wrong
              }
            } else {
              stores_filtered = [...$stores];
            }
        }
        
        //filter by FLOOR
        filterByFloor(fob_selected);
      } else {
        stores_filtered = [...$stores];
      }
    }
    
    if (search_term) stores_filtered = $stores.filter(includesTerm(search_term));

    stores_by_initial = $locale ? groupByInitial( stores_filtered, $locale ) : [];
    page = 1;
    infinite_id += 1;
    stores_shown = stores_filtered.slice(0, size);
  }
}

  const includesTerm = (term: string) => ({ name, keywords }: ManageAPI.Store) => {
    return name.toLowerCase().includes(term.toLowerCase()) || keywords.toLowerCase().includes(term.toLowerCase());
  };
  
  function infiniteHandler() {
    page++;
    stores_shown = stores_filtered.slice(0, size * page);
  }
</script>

{#if $stores && $locale}
  <div class="row">
    {#if display == 'normal'}
      <div class="col-lg-4">
        <ToggleButtonGroup
          btn_selected={fob_selected}
          {request_store_key}
          {lineOfBusinessID}
          on:select={changeFilter}
          on:search={changeSearch}
        />
      </div>
      <div class="col-lg-8 mt-3 mt-lg-0 px-4 px-lg-2">
        <div class="row">
          {#if stores_shown.length}
            {#each stores_shown as store}
              <StoreCard {request_store_key} {store} isSearchResult={false} />
            {/each}  
          {/if}
        </div>
        {#if stores_shown.length < stores_filtered.length}
          <button
            on:click={infiniteHandler}
            type="button"
            class="show-more purple-outline-button mx-auto d-block fit-content"
          >
            {t`show-more`}
          </button>
        {/if}
      </div>
    {:else if display == 'alphabetical-order' && $stores.length }
      {#each stores_by_initial as category}
        <StoreCategory {request_store_key} {category} />
      {/each}
    {:else if display == 'opening-hours' && $stores.length }
      {#each $stores as store}
        <StoreOpeningHours {request_store_key} {store} />
      {/each}
    {/if}
  </div>  
{/if}
